/**
 * ExpeditionCompleteModal.jsx - Shows expedition rewards when completed
 */

import React from 'react';
import { resources } from '../../data/resources';
import { ALL_ITEMS } from '../../data/dungeon/items';
import { DUNGEON_RESOURCES } from '../../data/dungeon/resources';
import './ExpeditionCompleteModal.css';

// Helper to get resource info - checks kingdom resources first, then dungeon resources
const getResourceInfo = (resourceId) => {
  if (resources[resourceId]) return resources[resourceId];
  if (DUNGEON_RESOURCES[resourceId]) return DUNGEON_RESOURCES[resourceId];
  return { name: resourceId, icon: '📦', rarity: 'common' };
};

// Helper to get item info - check both items and dungeon resources
const getItemInfo = (itemId) => {
  if (ALL_ITEMS[itemId]) return ALL_ITEMS[itemId];
  if (DUNGEON_RESOURCES[itemId]) return DUNGEON_RESOURCES[itemId];
  return { name: itemId, icon: '📦', rarity: 'common' };
};

export function ExpeditionCompleteModal({ completion, onClose }) {
  if (!completion) return null;

  const { rewards, isCriticalSuccess, expGranted, drops } = completion;

  // Check if we have any rewards to show
  const hasResources = rewards && Object.keys(rewards).length > 0;
  const hasDrops = drops && drops.length > 0;

  return (
    <div className="expedition-modal-overlay" onClick={onClose}>
      <div className="expedition-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`expedition-modal-header ${isCriticalSuccess ? 'critical' : ''}`}>
          <span className="expedition-icon">🏕️</span>
          <h2>Expedition Complete!</h2>
          {isCriticalSuccess && <span className="critical-badge">CRITICAL SUCCESS!</span>}
        </div>

        {/* XP Section */}
        {expGranted > 0 && (
          <div className="expedition-section xp-section">
            <div className="section-label">Experience Gained</div>
            <div className="xp-reward">
              <span className="xp-icon">⭐</span>
              <span className="xp-value">+{expGranted} XP</span>
            </div>
          </div>
        )}

        {/* Kingdom Resources Section */}
        {hasResources && (
          <div className="expedition-section resources-section">
            <div className="section-label">Resources Gathered</div>
            <div className="resources-grid">
              {Object.entries(rewards).map(([resourceId, amount]) => {
                const resourceInfo = getResourceInfo(resourceId);
                return (
                  <div key={resourceId} className="resource-reward">
                    <span className="resource-icon">{resourceInfo.icon}</span>
                    <span className="resource-amount">+{amount}</span>
                    <span className="resource-name">{resourceInfo.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Materials/Items Dropped Section */}
        {hasDrops && (
          <div className="expedition-section drops-section">
            <div className="section-label">Materials Found</div>
            <div className="drops-grid">
              {drops.map((drop, idx) => {
                const itemInfo = getItemInfo(drop.item);
                return (
                  <div key={idx} className={`drop-item rarity-${itemInfo.rarity || 'common'}`}>
                    <span className="drop-icon">{itemInfo.icon}</span>
                    <span className="drop-qty">x{drop.qty}</span>
                    <span className="drop-name">{itemInfo.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Close Button */}
        <button className="expedition-close-btn" onClick={onClose}>
          Collect Rewards
        </button>
      </div>
    </div>
  );
}

export default ExpeditionCompleteModal;
